import { NextPageContext } from 'next';
import { az, ru } from 'date-fns/locale';
import { format } from 'date-fns';
import { type i18n } from 'i18next';
import {
  CATEGORIES_SLUGS_COUNT,
  Languages,
  OPERATOR_CODES,
} from '@/constants/common';
import { ROUTES } from '@/constants/routes';
import { CONFIG } from '@/constants/config';
import {
  HostSum,
  MergeAdResultsOptions,
  OpenNewWindowParams,
} from '@/utils/helpers.types';
import { AdDetails, GetAdsResponse } from '@/api/types/ads.types';
import { COUNT_TO_FIND_DUPLICATES } from '@/hooks/useAdsWithInfiniteScroll/useAdsWithInfiniteScroll.constants';
import { Ad } from '@/api/types/common.types';
import {
  CATEGORY_SLUG_INDEX,
  SUBCATEGORY_SLUG_INDEX,
} from '@/hooks/useCurrentCategory/useCurrentCategory.constants';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { GetUserBookmarksResponse } from '@/api/types/user.types';
import { Category, PropertyOption } from '@/api/types/categories.types';
import {
  GetCollectionDataQuery,
  GetCollectionsListQuery,
} from '@/features/BookmarkCollections/api';

export const setSuffix = (i18next: i18n, val: string, prefix: string) => {
  const count = parseInt(val, 10);
  const lang = i18next.language;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
  const suffix = i18next.services.pluralResolver.getSuffix(
    lang,
    count
  ) as string;

  return `${prefix}${suffix || ''}`;
};

export const getReversedLangDefinition = (lang: string) => {
  switch (lang) {
    case 'ru':
      return {
        short: 'az',
        long: 'Azərbaycan dili',
      };
    default:
      return {
        short: 'ru',
        long: 'Русский язык',
      };
  }
};

export const hostnameWithoutLang = (host: string) => {
  const lang = host.split('.')[0];
  let pureHost = host;
  if (CONFIG.languages.includes(lang)) {
    pureHost = host.split('.').splice(1).join('.');
  }
  return pureHost;
};

export const cleanupSeoUrl = (url: string, isRelative = false) => {
  if (isRelative) {
    const [baseUrl, queryString] = url.split('?');
    if (!queryString) return baseUrl;

    const searchParams = new URLSearchParams(queryString);
    searchParams.delete('keywords_source');

    const newQueryString = searchParams.toString();
    return newQueryString ? `${baseUrl}?${newQueryString}` : baseUrl;
  }

  const result = new URL(url);
  result.searchParams.delete('keywords_source');

  return result.toString();
};

export const makeUrlWithLang = ({
  protocol,
  host,
  path,
  lang,
}: {
  protocol: string;
  host: string;
  path: string;
  lang?: string;
}) => {
  const defaultLang = 'az';
  const language = !lang || lang === defaultLang ? '' : `${lang}.`;
  return `${protocol}://${language}${hostnameWithoutLang(host)}${path}`;
};

export const makeHrefForPhone = (phone: string) => {
  const digitsOnly = phone.replace(/\D/g, '');
  const withoutLeadingZero = digitsOnly.replace(/^0+/, '');
  return `tel:+994${withoutLeadingZero}`;
};

export const sleep = (ms: number) =>
  // eslint-disable-next-line no-promise-executor-return
  new Promise(resolve => setTimeout(resolve, ms));

export function formatPhoneNumber(
  input?: string | ReadonlyArray<string> | number
) {
  if (typeof input !== 'string') {
    return '';
  }

  const digitsOnly = input.trim().replace(/\D/g, '');
  const match = digitsOnly.match(/^(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})(\d*)$/);

  if (!match) {
    return input.slice(0, -1);
  }

  const [, part1, part2, part3, part4] = match;

  const formattedParts = [
    part1 ? `(${part1}` : '',
    part2 ? `) ${part2}` : '',
    part3 ? `-${part3}` : '',
    part4 ? `-${part4}` : '',
  ];

  return formattedParts.join('');
}

export const toBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error('Failed to convert the file to base64.'));
    };

    reader.readAsDataURL(file);
  });
};

export const isProduction = () =>
  process.env.VERCEL_ENV === 'production' || CONFIG.isProduction === 'true';

export const formatNumber = (
  num?: number | null,
  decimalPlaces = 0,
  showDecimalOnlyIfPresent = false
) => {
  if (num === undefined || num === null) return '';

  const hasDecimal = num % 1 !== 0;
  const options = {
    minimumFractionDigits:
      showDecimalOnlyIfPresent && !hasDecimal ? 0 : decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };

  const formattedNumber = num.toLocaleString('ru-RU', options);

  return formattedNumber.replace(/\u00A0/g, ' ');
};

export const extractOperatorCodeFromPhone = (phone: string) => {
  const pattern = /\((\d{3})/;
  const match = pattern.exec(phone);

  if (match && match.length > 1) {
    return match[1];
  }

  return null;
};

export const checkCodeOperatorValidity = (code: string | null) => {
  if (code === null) {
    return false;
  }

  return OPERATOR_CODES.includes(code);
};

export function getAdPagePath(slug?: string | string[]) {
  const slugStr = Array.isArray(slug) ? slug?.join('/') : `/${slug}`;
  return `${ROUTES.ad}/${slugStr}`;
}

export const onlyNumbers = (
  value: string | null | undefined,
  maxLength?: number
) => {
  const onlyNumbersValue = value ? value.replace(/[^0-9]/g, '') : '';
  return maxLength ? onlyNumbersValue.slice(0, maxLength) : onlyNumbersValue;
};

export const addSpacesBetweenNumbers = (value: string, maxLength?: number) => {
  return onlyNumbers(value, maxLength).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export const filterNonNumericChars = (value: string | undefined | null) => {
  if (!value) return '';
  return value.replace(/[^\d.,]/g, '');
};

export const getDataStatAttr = ({
  dataStat,
  condition = true,
}: {
  dataStat?: string;
  condition?: boolean;
}) => {
  return condition && dataStat ? { 'data-stat': dataStat } : {};
};

export function encodeString(string: string) {
  return Buffer.from(string).toString('base64');
}

export const getLoginUrlWithReturn = (
  onSuccessReturnUrl: string,
  backUrl?: string,
  phone?: string
) => {
  if (!CONFIG.protocol || !CONFIG.authHost || typeof window === 'undefined') {
    return '';
  }

  const returnTo = encodeString(window.location.origin + onSuccessReturnUrl);

  const backTo = encodeString(
    window.location.origin + (backUrl ?? ROUTES.home)
  );

  const searchParams = new URLSearchParams({
    return_to: returnTo,
    back_to: backTo,
  });

  if (phone) {
    searchParams.append('phone', phone);
  }

  return makeUrlWithLang({
    protocol: CONFIG.protocol,
    host: CONFIG.authHost,
    path: `?${searchParams.toString()}`,
    lang: Languages.Az,
  });
};

export const getUrlBasedOnAuth = (
  isAuthenticated: boolean,
  onSuccessReturnUrl: string,
  backUrl: string
) => {
  return isAuthenticated
    ? onSuccessReturnUrl
    : getLoginUrlWithReturn(onSuccessReturnUrl, backUrl);
};

export const hostname = (lang: string, host: string) => {
  return lang === 'az' ? host : `${lang}.${host}`;
};

export const normalizePath = (asPath?: string) => {
  if (!asPath) return '';

  try {
    const urlObject = new URL(asPath);
    urlObject.search = '';
    return urlObject.toString();
  } catch (error) {
    return asPath;
  }
};

export const getHostFromReq = (req: NextPageContext['req']) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { headers } = req!;
  const reqHost =
    headers.host || headers['x-real-host'] || headers['x-forwarded-host'];

  const lang = reqHost?.includes('ru.') ? 'ru' : 'az';

  return (
    reqHost?.includes('react.tap.az') && CONFIG.host
      ? hostname(lang, CONFIG.host)
      : reqHost
  ) as string;
};

export function getLangFromHost(host: string): Languages {
  const currentLang = host.split('.')[0];
  return CONFIG.languages.includes(currentLang)
    ? (currentLang as Languages)
    : Languages.Az;
}

export function getCurrentUrlFromContext(context: NextPageContext) {
  const host = getHostFromReq(context.req);
  const isLocalhost = host.includes('localhost');
  const protocol = context.req?.headers['x-forwarded-proto'] || CONFIG.protocol;
  const lang = getLangFromHost(host);
  return makeUrlWithLang({
    protocol: Array.isArray(protocol) ? protocol[0] : protocol,
    host: isLocalhost ? host : CONFIG.host || '',
    path: context.asPath || '',
    lang,
  });
}

export const makeHostSum = ({
  asPath = '/',
  req,
}: {
  asPath?: string;
  req?: NextPageContext['req'];
}): HostSum => {
  const host = req ? getHostFromReq(req) : window.location.host;
  const protocol = req
    ? req.headers['x-forwarded-proto'] || 'https'
    : window.location.protocol.slice(0, -1);

  const currentUrl = `${protocol}://${host}${asPath}`;

  return {
    currentUrl,
    protocol: Array.isArray(protocol) ? protocol[0] : protocol,
    host,
    path: normalizePath(asPath),
  };
};

export function removeLangFromUrl(url: string): string {
  const langRegex = /^(https?:\/\/)[a-z]{2}\./i;

  return url.replace(langRegex, '$1');
}

export function addLangToUrl(baseUrl: string, lang: string): string {
  if (lang === 'az') return baseUrl;

  const domain = baseUrl
    .replace(/\/$/, '')
    .replace(/^(https?:\/\/)([^/]+)(\/.*)?$/, '$2');

  return `${baseUrl.replace(domain, `${lang}.${domain}`)}`;
}

export function isJestRunning() {
  return process.env.JEST_WORKER_ID !== undefined;
}

export function isCI() {
  return !!process.env.CI;
}

export function sortByObjectProperty<T extends Record<string, unknown>>(
  arr: T[],
  property: keyof T,
  reverse = false
): T[] {
  const copy = arr.slice();

  return copy.sort((a, b) => {
    const compareResult = a[property] < b[property] ? 1 : -1;
    return reverse ? -compareResult : compareResult;
  });
}

export function transformCapitalAzI(str: string) {
  return str.replace(/İ/g, 'I');
}

export function cutSentenceFor2Parts(sentence: string, cutSubstring: string) {
  const lowerCasedSentence = transformCapitalAzI(sentence).toLowerCase();
  const lowerCasedCutSubstring =
    transformCapitalAzI(cutSubstring).toLowerCase();
  const index = lowerCasedSentence.indexOf(lowerCasedCutSubstring);

  const firstPart = sentence.slice(0, index + cutSubstring.length);
  const secondPart = sentence.slice(index + cutSubstring.length);
  return [firstPart, secondPart];
}

export function isProductionClient() {
  const href = typeof window !== 'undefined' && window.location.href;
  const isProductionHref =
    typeof href === 'string' &&
    (href?.startsWith('https://tap.az') ||
      href?.startsWith('https://ru.tap.az'));

  return { href, isProductionHref };
}

export function isStagingClient() {
  const href = typeof window !== 'undefined' && window.location.href;
  const isStagingHref =
    typeof href === 'string' &&
    (href.includes('azstaging.net') || href.includes('azstage.in'));

  return { href, isStagingHref };
}

export function isEndsWithNumbers(str: string) {
  const regex = /\d+$/;
  return regex.test(str);
}

export function isOnlyNumbers(str: string) {
  return /^\d+$/.test(str);
}

export function cleanPhoneNumber(phoneNumber: string) {
  return phoneNumber.replace(/[()\s-]/g, '');
}

export function startCall(phoneNumber: string) {
  document.location.href = `tel:${cleanPhoneNumber(phoneNumber)}`;
}

export const getPathWithoutHash = (path: string | null) => {
  return path?.split('#')[0] ?? null;
};

export function removeBaseUrl(url: string) {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
  } catch (error) {
    return url;
  }
}

export function startsWithHttp(url: string) {
  const regex = /^https?:\/\//;
  return regex.test(url);
}

export function isDeepLink(url: string) {
  const regex = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//;
  return !startsWithHttp(url) && regex.test(url);
}

export function getBaseUrl(fullUrl: string): string {
  try {
    const url = new URL(fullUrl);
    const baseUrlWithoutPort = `${url.protocol}//${url.hostname}`;
    return url.port ? `${baseUrlWithoutPort}:${url.port}` : baseUrlWithoutPort;
  } catch (error) {
    return '';
  }
}

export function openNewWindow(params?: OpenNewWindowParams): Window | null {
  const { width = 600, height = 800, top = 0, left = 0 } = params || {};
  return window.open(
    '',
    '_blank',
    `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`
  );
}

export const removeWhitespaces = (str: string) => str.replace(/[()\s-]/g, '');

export function getLocale() {
  if (typeof window !== 'undefined') {
    const url = window.location.href;
    const regex = /^https?:\/\/(ru)\./;
    return regex.test(url) ? ru : az;
  }
}

export function formatDate(date?: Date | string, formatStr?: string) {
  return format(new Date(date ?? ''), formatStr ?? 'dd MMMM HH:mm', {
    locale: getLocale(),
  });
}

export function formatPhoneNumberFromBE(phoneNumber?: string): string {
  if (!phoneNumber) {
    return '';
  }
  const match = phoneNumber.match(/^(\d{3})(\d{3})(\d{2})(\d{2})$/);
  if (match) {
    return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
  }
  return phoneNumber;
}

export function calculatePercentageOff(number: number, percentage: number) {
  return number - (number * percentage) / 100;
}

export const formatNumberString = (
  value: string | undefined | null,
  replaceComma = false
) => {
  if (!value) return '';
  return replaceComma
    ? value.replace(/\s/g, '').replace(/,/g, '.')
    : value.replace(/\s/g, '');
};

export function generateRandomNumber(max = 10_000) {
  return Math.floor(Math.random() * max) + 1;
}

export function generateRandomCursor(length = 3): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  return Array.from(
    { length },
    () => characters[Math.floor(Math.random() * characters.length)]
  ).join('');
}

export function isBooleanString(value: string | null) {
  return ['true', 'false'].includes(value ?? '');
}

export function chunkArray<T>(array: T[], chunkSize: AdsChunkSize): T[][] {
  const length = Math.ceil(array.length / chunkSize);
  return Array.from({ length }, (_, i) =>
    array.slice(i * chunkSize, i * chunkSize + chunkSize)
  );
}

export function getNewUniqueAds(prevAds: Ad[], newAds: Ad[]) {
  const lastAdsSet = new Set(
    prevAds.slice(-COUNT_TO_FIND_DUPLICATES).map(ad => ad.id)
  );
  return newAds.filter(ad => !lastAdsSet.has(ad.id)) || [];
}

export function mergeAdsResults(
  prevResult: GetAdsResponse,
  fetchMoreResult: GetAdsResponse,
  options?: MergeAdResultsOptions
) {
  const { shouldFilterDuplicates = false, chunkSize } = options ?? {};
  if (!fetchMoreResult?.ads || !prevResult?.ads) return prevResult;

  const newAds = shouldFilterDuplicates
    ? getNewUniqueAds(prevResult.ads.nodes, fetchMoreResult.ads.nodes)
    : fetchMoreResult.ads.nodes;

  const paddedNewAds = chunkSize
    ? padAdsToChunkSize(newAds, chunkSize)
    : newAds;

  return {
    ...prevResult,
    ads: {
      ...prevResult.ads,
      nodes: [...prevResult.ads.nodes, ...paddedNewAds],
      pageInfo: fetchMoreResult.ads.pageInfo,
    },
  };
}

export function padAdsToChunkSize(ads: Ad[], size: number): Ad[] {
  return Array.from({ length: size }, (item, index) => ads[index] || null);
}

export const getCategoryPath = (currentPath = '') => {
  const parts = currentPath
    .split(/[?#]/)[0]
    .split('/')
    .reduce<string[]>(
      (acc, part, index) =>
        index > 0 && part !== 'vip' ? [...acc, part] : acc,
      []
    );

  if (!parts.length || !currentPath.startsWith(ROUTES.ad)) {
    return null;
  }

  return parts.slice(0, CATEGORIES_SLUGS_COUNT).join('/');
};

export const getNewAdPageHref = (currentPath?: string) => {
  if (!currentPath) return ROUTES.newAd;

  const categoryPath = getCategoryPath(currentPath);
  return categoryPath ? `/${categoryPath}/new` : ROUTES.newAd;
};

export const normalizeTargetingParameter = (str?: string): string => {
  if (!str) return '';

  const letters: Record<string, string> = {
    Ə: 'E',
    Ü: 'U',
    Ö: 'O',
    Ğ: 'Gh',
    Ş: 'Sh',
    Ç: 'Ch',
    İ: 'I',
    ə: 'e',
    ü: 'u',
    ö: 'o',
    ğ: 'gh',
    ş: 'sh',
    ç: 'ch',
    ı: 'i',
  };

  return str
    .split('')
    .map(letter => letters[letter] ?? letter)
    .join('')
    .replace(/\s+/g, '-');
};

export function capitalize(str = '') {
  return str && str[0].toUpperCase() + str.slice(1);
}

export function extractTargetParams(adDetails?: AdDetails) {
  if (!adDetails) {
    return null;
  }

  const slugs = adDetails.path.replace('/elanlar/', '').split('/');

  return {
    category: slugs[CATEGORY_SLUG_INDEX],
    subcategory: slugs[SUBCATEGORY_SLUG_INDEX],
    price: adDetails.price,
    make: adDetails.azProperties.find(property => property.name === 'Marka')
      ?.value,
    new: adDetails.azProperties.find(property => property.name === 'Yeni?')
      ?.value,
    type: adDetails.azProperties.find(
      property => property.name === 'Malın tipi'
    )?.value,
    city: adDetails.azRegion,
  };
}

export function mergeBookmarksResults(
  prevResult: GetUserBookmarksResponse,
  fetchMoreResult: GetUserBookmarksResponse
): GetUserBookmarksResponse {
  const prevBookmarks = prevResult?.currentUser?.bookmarks;
  const newBookmarks = fetchMoreResult?.currentUser?.bookmarks;
  const newList = newBookmarks.nodes;

  if (!prevBookmarks || !newBookmarks || newList.length === 0) {
    return prevResult;
  }

  const existingIds = new Set(prevBookmarks.nodes.map(node => node.id));
  const uniqueNewNodes = newList.filter(node => !existingIds.has(node.id));

  return {
    ...prevResult,
    currentUser: {
      ...prevResult.currentUser,
      bookmarks: {
        ...prevBookmarks,
        nodes: [...prevBookmarks.nodes, ...uniqueNewNodes],
        pageInfo: newBookmarks.pageInfo,
        totalCount: newBookmarks.totalCount,
      },
    },
  };
}

export function hasCategoryType(
  item: Category | PropertyOption
): item is Category {
  return 'id' in item;
}

export const simplifyStr = (str: string): string => {
  const umlauts: Record<string, string> = {
    Ç: 'C',
    ç: 'c',
    Ə: 'E',
    ə: 'e',
    Ğ: 'G',
    ğ: 'g',
    ı: 'i',
    İ: 'I',
    i̇: 'i',
    Ö: 'O',
    ö: 'o',
    Ş: 'S',
    ş: 's',
    Ü: 'U',
    ü: 'u',
  };

  const umlautsRegex = new RegExp(Object.keys(umlauts).join('|'), 'g');

  return str
    .replace(umlautsRegex, (s: string) => umlauts[s] || s)
    .toLowerCase();
};

export function mergeCollectionAdsResults(
  prevResult: GetCollectionDataQuery,
  fetchMoreResult: GetCollectionDataQuery
): GetCollectionDataQuery {
  const prevAds = prevResult?.bookmarkCollectionDetails?.ads;
  const newAds = fetchMoreResult?.bookmarkCollectionDetails?.ads;
  const newList = newAds.nodes;

  if (!prevAds || !newAds || newList.length === 0) {
    return prevResult;
  }

  const existingIds = new Set(prevAds.nodes.map(node => node.id));
  const uniqueNewNodes = newList.filter(node => !existingIds.has(node.id));

  return {
    ...prevResult,
    bookmarkCollectionDetails: {
      ...prevResult.bookmarkCollectionDetails,
      ads: {
        ...prevAds,
        nodes: [...prevAds.nodes, ...uniqueNewNodes],
        pageInfo: newAds.pageInfo,
        totalCount: newAds.totalCount,
      },
    },
  };
}

export function mergeCollectionsList(
  prev: GetCollectionsListQuery,
  fetchMoreResult: GetCollectionsListQuery
): GetCollectionsListQuery {
  const prevCollections = prev?.currentUser?.bookmarkCollections;
  const newCollections = fetchMoreResult?.currentUser?.bookmarkCollections;
  const newList = newCollections.nodes;

  if (!prevCollections || !newCollections || newList.length === 0) {
    return prev;
  }

  const existingIds = new Set(prevCollections.nodes.map(node => node.id));
  const uniqueNewNodes = newList.filter(node => !existingIds.has(node.id));

  return {
    ...prev,
    currentUser: {
      ...prev.currentUser,
      bookmarkCollections: {
        ...prevCollections,
        nodes: [...prevCollections.nodes, ...uniqueNewNodes],
        pageInfo: newCollections.pageInfo,
        totalCount: newCollections.totalCount,
      },
    },
  };
}

export function getFulfilledValue<T>(
  result: PromiseSettledResult<T>
): T | null {
  return result.status === 'fulfilled' ? result.value : null;
}
